import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {Button, ListItemText, Menu, MenuItem, Paper, Stack} from "@mui/material";
import Typography from "@mui/material/Typography";
import type {Survey, SurveyType} from "crqlar-react-common/dist/models/survey/survey.model";
import type {SurveyTemplateDefinition} from "crqlar-react-common/dist/models/survey/surveyTemplate.model";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {blankSurveyDraft} from "../details/surveyDraft.util";

export const BLANK_TEMPLATE_KEY = 'BLANK';

interface Props {
    survey: Survey
    onChange: (survey: Survey) => void
    /**
     * The catalogue, already built in one language by the API. Nothing in here is translated by the
     * dashboard: `title`, `description` and the prototype's name are finished copy.
     */
    templates: SurveyTemplateDefinition[]
    selectedKey: string
    onSelectedKeyChange: (key: string) => void
    nameIsSuggestion: boolean
    onNameIsSuggestionChange: (suggested: boolean) => void
    /** Only templates of this type are offered; the type select above this picker owns it. */
    filterType?: SurveyType
    disabled?: boolean
}

function SurveyTemplatePicker({
    survey,
    onChange,
    templates,
    selectedKey,
    onSelectedKeyChange,
    nameIsSuggestion,
    onNameIsSuggestionChange,
    filterType,
    disabled,
}: Props) {
    const {t} = useTranslation();

    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const menuOpen = !!menuAnchor;

    const selected = templates.find(entry => entry.key === selectedKey);
    const offered = templates.filter(entry => !filterType || entry.survey.type === filterType);

    /**
     * Puts the template's prototype into the draft so the preview shows the real survey. Only the
     * key travels back to the API on save — the prototype's ids are placeholders and the survey is
     * built server-side.
     */
    const apply = (key: string) => {
        const definition = templates.find(entry => entry.key === key);
        const base = definition ? definition.survey : blankSurveyDraft();

        const keepTyped = !!survey.name?.trim() && !nameIsSuggestion;
        const suggestion = definition ? definition.survey.name : t('New empty survey');
        const name = keepTyped ? survey.name : suggestion;

        onSelectedKeyChange(key);
        onNameIsSuggestionChange(!keepTyped);
        onChange({
            ...base,
            name,
            // A template carries its own type; going blank keeps whatever the select says.
            type: definition ? base.type : survey.type,
        });
    };

    return (
        <Stack gap={1.5}>
            <Typography variant={'subtitle2'} color={'text.secondary'}>
                {t('Start with')}
            </Typography>

            <Paper variant={'outlined'} sx={{p: 2}}>
                <Stack gap={0.25}>
                    <Typography variant={'body1'} sx={{fontWeight: 500}}>
                        {selected ? selected.title : t('New empty survey')}
                    </Typography>
                    <Typography variant={'body2'} color={'text.secondary'}>
                        {selected ? selected.description : t('Start from scratch')}
                    </Typography>
                </Stack>

                <Stack direction={'row'} flexWrap={'wrap'} gap={0.5} sx={{mt: 1.5}}>
                    <Button
                        id={'survey_template_button'}
                        variant={'text'}
                        color={'inherit'}
                        endIcon={<ExpandMoreIcon/>}
                        disabled={disabled}
                        aria-haspopup={'menu'}
                        aria-controls={menuOpen ? 'survey_template_menu' : undefined}
                        aria-expanded={menuOpen ? 'true' : undefined}
                        onClick={event => setMenuAnchor(event.currentTarget)}
                        sx={{color: 'text.secondary'}}
                    >
                        {t('Use a template')}
                    </Button>

                    {!!selected && (
                        <Button
                            variant={'text'}
                            color={'inherit'}
                            startIcon={<CloseIcon/>}
                            disabled={disabled}
                            onClick={() => apply(BLANK_TEMPLATE_KEY)}
                            sx={{color: 'text.secondary'}}
                        >
                            {t('Remove template')}
                        </Button>
                    )}
                </Stack>

                <Menu
                    id={'survey_template_menu'}
                    anchorEl={menuAnchor}
                    open={menuOpen}
                    onClose={() => setMenuAnchor(null)}
                    MenuListProps={{'aria-labelledby': 'survey_template_button'}}
                    PaperProps={{sx: {minWidth: 300, maxWidth: 440}}}
                >
                    {offered.length === 0 && (
                        <MenuItem disabled>{t('No templates available')}</MenuItem>
                    )}
                    {offered.map(definition => (
                        <MenuItem
                            key={definition.key}
                            selected={definition.key === selectedKey}
                            sx={{whiteSpace: 'normal'}}
                            onClick={() => {
                                setMenuAnchor(null);
                                apply(definition.key);
                            }}
                        >
                            <ListItemText
                                primary={definition.title}
                                secondary={definition.description}
                            />
                        </MenuItem>
                    ))}
                </Menu>
            </Paper>
        </Stack>
    );
}

export default SurveyTemplatePicker;
